import { createSelector } from '@reduxjs/toolkit';

import selectCourseTabs from '../../components/NavigationBar/data/selectors';
import { DENIED, LOADED } from '../../components/NavigationBar/data/slice';
import { PostsStatusFilter, ThreadType } from '../../data/constants';

export const isCourseStatusValid = (courseStatus) => [DENIED, LOADED].includes(courseStatus);

export const selectAnonymousPostingConfig = state => ({
  allowAnonymous: state.config.allowAnonymous,
  allowAnonymousToPeers: state.config.allowAnonymousToPeers,
});

// Moderation privileges include: Discussion Admin, Discussion Moderator, and Global Staff
// Course Staff and Course Admin are explicitly EXCLUDED and should behave like learners
export const selectUserHasModerationPrivileges = state => (
  state.config.hasModerationPrivileges || state.config.isUserAdmin
);

export const selectUserHasBulkDeletePrivileges = state => state.config.hasBulkDeletePrivileges;

export const selectUserIsStaff = state => state.config.isUserAdmin;

export const selectUserIsGroupTa = state => state.config.isGroupTa;

export const selectUserIsCommunityTa = state => state.config.userRoles?.includes('Community TA');

export const selectConfigLoadingStatus = state => state.config.status;

export const selectUserRoles = state => state.config.userRoles;

// Course-level permissions: Global Staff, Discussion Admin, Discussion Moderator
// EXCLUDES: Course Instructor, Course Staff, Group TA, Community TA, Learners
export const selectUserCanBanAtCourseLevel = createSelector(
  [
    state => state.config.isUserAdmin,
    state => state.config.hasModerationPrivileges,
  ],
  (isUserAdmin, hasModerationPrivileges) => (
    isUserAdmin || hasModerationPrivileges
  ),
);

// Org-level permissions: Global Staff, Discussion Admin
// EXCLUDES: Discussion Moderator, Course Instructor, Course Staff, Group TA, Community TA, Learners
export const selectUserCanBanAtOrgLevel = createSelector(
  [
    state => state.config.isUserAdmin,
    selectUserRoles,
  ],
  (isUserAdmin, userRoles = []) => (
    isUserAdmin
      || userRoles.includes('Administrator')
  ),
);

export const selectDivisionSettings = state => state.config.settings;

export const selectGroupAtSubsection = state => state.config.groupAtSubsection;

export const selectIsCourseAdmin = state => state.config.isCourseAdmin;

export const selectIsCourseStaff = state => state.config.isCourseStaff;

export const selectEnableInContext = state => state.config.enableInContext;

export const selectIsPostingEnabled = state => state.config.isPostingEnabled;

export const selectIsNotifyAllLearnersEnabled = state => state.config.isNotifyAllLearnersEnabled;

export const selectCaptchaSettings = state => state.config.captchaSettings;

export const selectIsEmailVerified = state => state.config.isEmailVerified;

export const selectContentCreationRateLimited = state => state.config.contentCreationRateLimited;

export const selectOnlyVerifiedUsersCanPost = state => state.config.onlyVerifiedUsersCanPost;

export const selectIsUserBanned = state => state.config.isUserBanned;

export const selectEnableDiscussionBan = state => state.config.enableDiscussionBan;

export const selectConfirmEmailStatus = state => state.threads.confirmEmailStatus;

export const selectPostStatus = state => state.comments.postStatus;

export const selectShouldShowEmailConfirmation = createSelector(
  [selectIsEmailVerified, selectOnlyVerifiedUsersCanPost],
  (isEmailVerified, onlyVerifiedUsersCanPost) => !isEmailVerified && onlyVerifiedUsersCanPost,
);

export const selectModerationSettings = state => ({
  postCloseReasons: state.config.postCloseReasons,
  editReasons: state.config.editReasons,
});

export const selectDiscussionProvider = state => state.config.provider;

export const selectPersonalMutedUsers = state => state.config.personalMutedUsers || [];

export const selectCourseWideMutedUsers = state => state.config.courseWideMutedUsers || [];

export function selectAreThreadsFiltered(state) {
  const { filters } = state.threads;

  if (filters.search) {
    return true;
  }

  return !(
    (filters.status === PostsStatusFilter.ALL || filters.status === PostsStatusFilter.ACTIVE)
    && filters.postType === ThreadType.ALL
  );
}

export function selectTopicThreadCount(topicId) {
  return state => {
    const topic = topicId && state.topics?.topics[topicId];
    if (!topic) {
      return 0;
    }
    return topic.threadCounts.question + topic.threadCounts.discussion;
  };
}

export function selectPostThreadCount(state) {
  return state.threads.totalThreads;
}

export const selectIsUserLearner = createSelector(
  selectUserHasModerationPrivileges,
  selectUserIsGroupTa,
  selectUserIsStaff,
  selectIsCourseAdmin,
  selectIsCourseStaff,
  selectCourseTabs,
  (
    userHasModerationPrivileges,
    userIsGroupTa,
    userIsStaff,
    userIsCourseAdmin,
    userIsCourseStaff,
    { courseStatus },
  ) => (
    (
      !userHasModerationPrivileges
      && !userIsGroupTa
      && !userIsStaff
      && !userIsCourseAdmin
      && !userIsCourseStaff
      && isCourseStatusValid(courseStatus)
    ) || false
  ),
);
